import { useLayoutEffect, useMemo, useRef } from "react";
import { flushSync } from "react-dom";
import { useStudio } from "@/contexts/StudioContext";
import { useAuth } from "@/contexts/AuthContext";

export type AppSurface = "dashboard" | "chats" | "data";

function storageKey(surface: AppSurface): string {
  return `oktogo_surface_${surface}`;
}

function isAllowed(id: string, studios: readonly { id: string }[]): boolean {
  if (id === "all") return true;
  return studios.some((s) => s.id === id);
}

/**
 * Apply session-scoped studio before navigating to a surface (avoids a second list refetch after mount).
 * Use `sync: true` from click handlers so `activeStudioId` matches the Chats query on first paint.
 */
export function applyStoredStudioForSurface(
  surface: AppSurface,
  studios: readonly { id: string }[],
  currentStudioId: string,
  setActiveStudioId: (id: string) => void,
  options?: { sync?: boolean }
): void {
  if (!studios.length) return;
  // Shell aggregate scope wins — do not replace All Studios with per-surface memory (FE-13).
  if (currentStudioId === "all") return;
  try {
    const raw = sessionStorage.getItem(storageKey(surface));
    if (!raw || !isAllowed(raw, studios) || raw === currentStudioId) return;
    const apply = () => setActiveStudioId(raw);
    if (options?.sync) flushSync(apply);
    else apply();
  } catch {
    /* ignore */
  }
}

/**
 * Slice E: remember studio scope per surface (sessionStorage). On entering Dashboard / Chats /
 * Studio Data, restores that surface's last `activeStudioId`; on leave, saves the current id.
 */
export function useSurfaceStudioScope(surface: AppSurface): void {
  const { activeStudioId, setActiveStudioId } = useStudio();
  const { user } = useAuth();
  const idRef = useRef(activeStudioId);
  idRef.current = activeStudioId;

  const studioIdsKey = useMemo(
    () =>
      (user?.studios ?? [])
        .map((s) => s.id)
        .sort()
        .join(","),
    [user?.studios],
  );

  useLayoutEffect(() => {
    if (!user?.id) return;
    const studios = user.studios ?? [];
    const key = storageKey(surface);
    try {
      const raw = sessionStorage.getItem(key);
      // Keep aggregate list when shell is All Studios (FE-13).
      if (raw && isAllowed(raw, studios) && raw !== idRef.current && idRef.current !== "all") {
        setActiveStudioId(raw);
      }
    } catch {
      /* ignore */
    }

    return () => {
      try {
        sessionStorage.setItem(key, idRef.current);
      } catch {
        /* ignore */
      }
    };
    // `studioIdsKey` reflects membership/order of `user.studios` without unstable array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see studioIdsKey
  }, [surface, user?.id, studioIdsKey, setActiveStudioId]);
}
